import { JSXElementConstructor, useMemo } from 'react';

import { ErrorBoundary as BaseErrorBoundary } from 'react-error-boundary';

import { useCoreProvider } from '../../../provider/useCoreProvider';
import { ErrorCard } from './ErrorCard';
import { ErrorContainer } from './ErrorContainer';
import { ErrorPage } from './ErrorPage';
import {
  BaseErrorProps,
  ErrorBoundaryCardProps,
  ErrorBoundaryContainerProps,
  ErrorBoundaryPageProps,
} from './types';

type DynamicErrorBoundaryProps = {
  children: React.ReactNode;
  FallBackComponent: JSXElementConstructor<BaseErrorProps & { resetError: () => void }>;
};
/** Dynamically switches between the provided error boundary component and the error boundary component from the core provider */
const DynamicErrorBoundary = ({
  children,
  FallBackComponent,
  ...props
}: DynamicErrorBoundaryProps) => {
  const { errorBoundaryConfig } = useCoreProvider();

  const ErrorBoundary = useMemo(() => {
    if (errorBoundaryConfig?.ErrorBoundaryComponent) {
      return errorBoundaryConfig.ErrorBoundaryComponent;
    }
    return null;
  }, [errorBoundaryConfig?.ErrorBoundaryComponent]);

  if (ErrorBoundary) {
    return (
      <ErrorBoundary fallback={(args) => <FallBackComponent {...args} {...props} />}>
        {children}
      </ErrorBoundary>
    );
  }

  return (
    <BaseErrorBoundary
      FallbackComponent={(args) => (
        <FallBackComponent {...args} resetError={() => args.resetErrorBoundary()} {...props} />
      )}
    >
      {children}
    </BaseErrorBoundary>
  );
};

const ErrorBoundaryPage = ({ children, ...props }: ErrorBoundaryPageProps) => {
  return (
    <DynamicErrorBoundary FallBackComponent={ErrorPage} {...props}>
      {children}
    </DynamicErrorBoundary>
  );
};

const ErrorBoundaryContainer = ({ children, ...props }: ErrorBoundaryContainerProps) => {
  return (
    <DynamicErrorBoundary FallBackComponent={ErrorContainer} {...props}>
      {children}
    </DynamicErrorBoundary>
  );
};

const ErrorBoundaryCard = ({ children, ...props }: ErrorBoundaryCardProps) => {
  return (
    <DynamicErrorBoundary FallBackComponent={ErrorCard} {...props}>
      {children}
    </DynamicErrorBoundary>
  );
};

const ErrorBoundary = () => {};

ErrorBoundary.Page = ErrorBoundaryPage;
ErrorBoundary.Container = ErrorBoundaryContainer;
ErrorBoundary.Card = ErrorBoundaryCard;

export { ErrorBoundary };
